import classNames from 'classnames';
import React, { Suspense, useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';

import {
  CreateDerivativesOrderRequest,
  DerivativesOrderProjection,
  DerivativesOrderSource,
  DerivativesOrderType,
  UpdateDerivativesOrderRequest,
} from 'common/api/autogenerated/trading-gateway';

import { StaticHeader, SvgIconUrl } from 'common/components';

import { useBowser, useDerivatives, useDerivativesOrders, useI18n, usePopup } from 'common/hooks';
import { ModalAction } from 'common/redux/modal/initialState';
import svgIcons from 'common/svgIcons';

import DerivativeDashboardProvider from 'views/derivatives/dashboard/DerivativeDashboard.provider';
import {
  DerivativeOrderFormMode,
  DerivativeOrderFormType,
  DerivativesPriceType,
} from 'views/derivatives/dashboard/components/OrderForm/types';
import PairInfoComponent from 'views/derivatives/orders/common/PairInfo/PairInfo.component';

import { formatDate } from 'utils/date';

import { useDispatch } from 'react-redux';
import { actionSetDerivativeOrderIsEditing } from 'views/derivatives/redux/derivatives.actions';
import './index.css';

const DerivativeOrderForm = React.lazy(
  () =>
    import(
      /* webpackChunkName: "DerivativeOrderForm" */ 'views/derivatives/dashboard/components/OrderForm/DerivativeOrderForm.component'
    ),
);

export type EditDerivativeOrderPopupProps = ModalAction & {
  order: DerivativesOrderProjection | undefined;
  mode: DerivativeOrderFormMode;
  onCancel?: () => void;
  update?: Partial<DerivativeOrderFormType>;
};

const EditDerivativeOrderPopup = React.memo((props: EditDerivativeOrderPopupProps) => {
  const { orders, onDelete, onEdit } = useDerivativesOrders();
  const dispatch = useDispatch();
  const { hide, submit } = usePopup();
  const { translate } = useI18n();
  const { isMobile } = useBowser();

  const { id, mode = props.mode } = useParams<{
    id: string;
    mode: DerivativeOrderFormMode;
  }>();

  const order = useMemo(
    () => props.order || orders.find((i) => Number(i.id) === Number(id)) || {},
    [id, orders, props.order],
  );

  const {
    price = 0,
    type,
    takeProfit,
    stopLoss,
    quantity = 0,
    reduceOnly,
    trigger,
    side: action,
    tradingPairCode,
    source,
  } = useMemo(() => order, [order]);

  const {
    stream: { symbol },
  } = useDerivatives(tradingPairCode);

  const [base, quoted] = useMemo(() => (tradingPairCode || '').split('-'), [tradingPairCode]);

  const code = useMemo(() => String(tradingPairCode), [tradingPairCode]);

  const pairFormatted = useMemo(() => symbol || '', [symbol]);

  const tpsl = useMemo(
    () => !!takeProfit?.triggerPrice || !!stopLoss?.triggerPrice,
    [stopLoss?.triggerPrice, takeProfit?.triggerPrice],
  );

  useEffect(
    function noteInReduxThatOrderIsEditingNow() {
      if (!order?.id) return;
      dispatch(actionSetDerivativeOrderIsEditing(order?.id));
      return () => {
        dispatch(actionSetDerivativeOrderIsEditing(null));
      };
    },
    [dispatch, id, order?.id],
  );

  const onSubmit = useCallback(
    (data: CreateDerivativesOrderRequest) => {
      const body: UpdateDerivativesOrderRequest = {
        contracts: data.contracts,
        price: data.price,
        takeProfit: data.takeProfit,
        stopLoss: data.stopLoss,
      };
      return onEdit(order.id, body)
        .then(() => {
          submit();
        })
        .catch((error: unknown) => {
          console.error('Error while editing order:', error);
        });
    },
    [onEdit, order.id, submit],
  );

  const handleDeleteOrder = useCallback(
    () =>
      onDelete(order.id).then(() => {
        submit();
      }),
    [submit, onDelete, order.id],
  );

  const sourceIsStopOrder = useMemo(() => source === DerivativesOrderSource.STOP_ORDER, [source]);

  const isDirtyFromProps = useMemo(() => props.update && !!Object.keys(props.update).length, [props.update]);

  const headerClassNames = classNames({
    'derivative-order-edit-popup__header': true,
    'derivative-order-edit-popup__header--edit':
      type === DerivativesOrderType.MARKET || (type === DerivativesOrderType.LIMIT && !sourceIsStopOrder),
    'derivative-order-edit-popup__header--delete':
      type === DerivativesOrderType.STOP_MARKET ||
      type === DerivativesOrderType.STOP_LIMIT ||
      (type === DerivativesOrderType.LIMIT && sourceIsStopOrder),
  });

  const renderMobileHeader = useCallback(
    () =>
      isMobile && (
        <StaticHeader className={headerClassNames}>
          <div
            onClick={hide}
            className='derivative-order-edit-popup__header-back'
            data-test='derivative-edit-order-back-btn'
          >
            <SvgIconUrl
              className='dashboards-card__arrow-icon'
              dataAttribute='dashboards-card__arrow-icon'
              symbol={svgIcons.Back()}
            />
          </div>
          <div className='derivative-order-edit-popup__header-title'>
            {translate.toText(
              type === DerivativesOrderType.LIMIT && sourceIsStopOrder
                ? 'mobile:derivative:edit:header:title:limit-source-stop'
                : `mobile:derivative:edit:header:title:${type}`,
            )}
          </div>
          <div className='derivative-order-edit-popup__header-id'>{`ID ${order.id}`}</div>
        </StaticHeader>
      ),
    [headerClassNames, hide, isMobile, order.id, sourceIsStopOrder, translate, type],
  );

  if (!order.id) return null;

  return (
    <div className='derivative-order-edit-popup'>
      <DerivativeDashboardProvider base={base} quoted={quoted} code={code}>
        {renderMobileHeader()}
        <div className='derivative-order-edit-popup__icons'>
          <PairInfoComponent
            pairName={pairFormatted}
            isRow={false}
            isColumn
            marginMode={order.marginMode}
            leverage={order.leverage ?? 0}
            side={order.side}
            type={order.type}
          />
        </div>
        <div className='derivative-order-edit-popup__date'>
          <span className='derivative-order-edit-popup__date-value'>
            {translate.toText('mobile:derivative:edit:datetime')}{' '}
            {formatDate(order.originCreatedOn, 'MMM dd, yyyy HH:mm')}
          </span>
        </div>
        <Suspense>
          <DerivativeOrderForm
            mode={mode}
            quantity={quantity}
            type={type}
            action={action}
            tpsl={tpsl}
            takeProfit={takeProfit?.triggerPrice}
            takeProfitTriggerPriceType={DerivativesPriceType.LAST} // 85523 TEMP CODE TO SHOW TEMPORARY UNAVAILABLE ERROR
            stopLoss={stopLoss?.triggerPrice}
            stopLossTriggerPriceType={DerivativesPriceType.LAST} // 85523 TEMP CODE TO SHOW TEMPORARY UNAVAILABLE ERROR
            triggerPrice={trigger?.price}
            triggerPriceType={DerivativesPriceType.LAST} // 85523 TEMP CODE TO SHOW TEMPORARY UNAVAILABLE ERROR
            reduceOnly={Boolean(reduceOnly)}
            limitPrice={price ?? undefined}
            onSubmit={onSubmit}
            onDelete={handleDeleteOrder}
            editLimitPrice={order.price}
            editTriggerPrice={order.trigger?.price}
            editQuantity={order.quantity}
            source={order.source}
            isDirtyFromProps={isDirtyFromProps}
            update={props.update}
          />
        </Suspense>
      </DerivativeDashboardProvider>
    </div>
  );
});

export default EditDerivativeOrderPopup;
